/* OnSumn — Calendar View
   4th view alongside Map / Split / Grid.

   Direction (2026-06-04 overhaul):
     The previous "amber heat tier" cell wash was illegible — opaque-on-opaque
     amber said nothing concrete to first-time visitors. Replaced with a
     "flyer wall" model:
       - Each cell shows what is ACTUALLY happening on that day.
       - Multi-color category dots (amber/orange/eucalyptus/fog-blue/violet/
         eucalyptus-light) replace heat opacity. Brand palette goes across
         cells, not amber-only.
       - Event count chip (e.g. "3" or "12+") sits bottom-right. Concrete.
       - Days with 4+ events get a one-line headliner snippet — the highest-
         interest event title, truncated.
       - Today = orange filled marker square + amber underline + bold ring.
       - Weekend (Fri/Sat) = faint amber-tinted background — subliminal
         "this is when most things happen" without shouting it.
       - Empty days stay quiet: just the date number.
     A small "WHAT THE COLORS MEAN" legend below the grid teaches the dot
     system once. Header gains an "X SHOWS IN {MONTH}" stat line + quick-
     filter chips (TODAY / TOMORROW / THIS WEEKEND) so users with no
     calendar muscle memory still find their way in.

   Implementation notes:
     - Pure React via window.React (project convention).
     - Grid + bucket math defers to lib/calendar-grid.js (window.CalendarGrid)
       — heatTiers is retained but no longer drives visuals; categoryMix +
       headlinerForDay are the new visual drivers.
     - Reuses window.EventCard for panel content.
     - Mobile (<768px) renders the panel as a bottom sheet (portal, z=1800).
     - useReducedMotion gates the animated open + scrollIntoView paths.
     - Occasion base rule (C8, 2026-10-01): the day an occasion IS carries a
       3px rule on its bottom edge + the label in its aria-label day-name
       clause. No text node. Spec: docs/design/2026-10-01-seasonal-rail-spec.md §5.
*/

// The seasonal roster (a zero-import leaf): every anchor in the displayed
// month is marked, live or not — a calendar that marks Christmas only after
// Nov 20 would change under the reader for no reason they can see (§5).
import { OCCASIONS, occasionAnchor } from '../lib/occasions.js';

function CalendarView({ events, darkMode, radius }) {
  const React = window.React;
  const CalendarGrid = window.CalendarGrid || {};
  const EventCard = window.EventCard;
  const useReducedMotion = window.useReducedMotion;

  const reducedMotion = (typeof useReducedMotion === 'function')
    ? useReducedMotion() : false;

  // Today (recomputed once per mount).
  const today = React.useMemo(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
  }, []);
  const todayKey = CalendarGrid.isoKey
    ? CalendarGrid.isoKey(today.y, today.m, today.d)
    : '';

  // Tomorrow key for quick filter.
  const tomorrowKey = React.useMemo(() => {
    const t = new Date(today.y, today.m, today.d + 1);
    return CalendarGrid.isoKey ? CalendarGrid.isoKey(t.getFullYear(), t.getMonth(), t.getDate()) : '';
  }, [today.y, today.m, today.d]);

  // Next weekend keys (upcoming Fri + Sat + Sun). If today IS Fri/Sat/Sun,
  // include today + remaining weekend.
  const weekendKeys = React.useMemo(() => {
    const base = new Date(today.y, today.m, today.d);
    const dow = base.getDay(); // 0=Sun..6=Sat
    let friOffset;
    if (dow <= 5) friOffset = 5 - dow;       // Mon..Fri → next/this Fri
    else friOffset = -1;                     // Sat → roll back to yesterday's Fri
    const keys = [];
    for (let i = friOffset; i <= friOffset + 2; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      if (CalendarGrid.isoKey) keys.push(CalendarGrid.isoKey(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    return keys;
  }, [today.y, today.m, today.d]);

  // Visible month state.
  const [cursor, setCursor] = React.useState({ year: today.y, month: today.m });

  // Selected day (ISO key). null = no panel open.
  const [selectedKey, setSelectedKey] = React.useState(null);

  // Bottom-sheet open state.
  const [sheetOpen, setSheetOpen] = React.useState(false);

  // isMobile gate.
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Grid shape.
  const grid = React.useMemo(() => {
    if (typeof CalendarGrid.monthMatrix !== 'function') {
      return { weeks: [], dayKeys: [] };
    }
    return CalendarGrid.monthMatrix(cursor.year, cursor.month, 0);
  }, [cursor.year, cursor.month]);

  // Occasion anchors in the displayed month: ISO key → roster entry. The
  // ANCHOR (occasionAnchor), never the window end — Halloween's window ends
  // Nov 1. A wrapping window anchored in the prior year can hold this
  // January's anchor (New Year's: occasionAnchor(entry, 2026) = '2027-01-01'),
  // so each entry is read for the displayed year AND the one before, and only
  // anchors inside this month survive. Null anchors (a table year off the
  // horizon) are skipped. Two anchors on one day: first in roster order wins.
  const occasionByKey = React.useMemo(() => {
    const out = new Map();
    const prefix = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-`;
    for (const entry of OCCASIONS) {
      for (const y of [cursor.year - 1, cursor.year]) {
        const iso = occasionAnchor(entry, y);
        if (iso && iso.startsWith(prefix) && !out.has(iso)) out.set(iso, entry);
      }
    }
    return out;
  }, [cursor.year, cursor.month]);

  // Bucket events by ISO day + derive per-day category mix + total counts.
  const { buckets, mixByKey, monthTotal } = React.useMemo(() => {
    if (typeof CalendarGrid.bucketEventsByDay !== 'function') {
      return { buckets: new Map(), mixByKey: new Map(), monthTotal: 0 };
    }
    const allBuckets = CalendarGrid.bucketEventsByDay(events || []);
    const visibleKeys = new Set(grid.dayKeys || []);
    const mixMap = new Map();
    let monthCount = 0;
    for (const [key, list] of allBuckets.entries()) {
      if (!visibleKeys.has(key)) continue;
      if (typeof CalendarGrid.categoryMix === 'function') {
        mixMap.set(key, CalendarGrid.categoryMix(list));
      }
      // Count only IN-MONTH cells for the header stat — bleed days belong
      // to the prev/next month, not the visible month.
      const parts = key.split('-').map(n => parseInt(n, 10));
      if (parts[0] === cursor.year && (parts[1] - 1) === cursor.month) {
        monthCount += list.length;
      }
    }
    return { buckets: allBuckets, mixByKey: mixMap, monthTotal: monthCount };
  }, [events, grid.dayKeys, cursor.year, cursor.month]);

  // Month nav handlers.
  const stepMonth = React.useCallback((delta) => {
    setCursor(prev => {
      let y = prev.year, m = prev.month + delta;
      if (m < 0) { m = 11; y -= 1; }
      else if (m > 11) { m = 0; y += 1; }
      return { year: y, month: m };
    });
    setSelectedKey(null);
    setSheetOpen(false);
  }, []);

  // Keyboard nav.
  const [focusKey, setFocusKey] = React.useState(null);
  const cellRefs = React.useRef({});

  React.useEffect(() => {
    if (!focusKey) return;
    const el = cellRefs.current[focusKey];
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: false });
  }, [focusKey]);

  const handleCellSelect = React.useCallback((key) => {
    setSelectedKey(key);
    if (window.innerWidth < 768) setSheetOpen(true);
  }, []);

  // Focus a day by DATE, not grid index. A target outside the visible month
  // (right from the 30th, up from the 1st, Home on a Tuesday) makes that
  // month visible first, so the cursor never lands on a ref-less bleed cell
  // and freezes — the pre-2026-09-29 dead-end at every month edge.
  const moveFocusTo = React.useCallback((d) => {
    const y = d.getFullYear(), m = d.getMonth();
    if (y !== cursor.year || m !== cursor.month) {
      setCursor({ year: y, month: m });
      setSelectedKey(null);
      setSheetOpen(false);
    }
    if (typeof CalendarGrid.isoKey === 'function') setFocusKey(CalendarGrid.isoKey(y, m, d.getDate()));
  }, [cursor.year, cursor.month, CalendarGrid]);

  const handleCellKeyDown = React.useCallback((ev, key, cellDate, dow) => {
    const STEP = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -dow, End: 6 - dow };
    if (ev.key in STEP) {
      ev.preventDefault();
      moveFocusTo(new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate() + STEP[ev.key]));
    } else if (ev.key === 'PageUp' || ev.key === 'PageDown') {
      ev.preventDefault();
      const t = new Date(cellDate.getFullYear(), cellDate.getMonth() + (ev.key === 'PageUp' ? -1 : 1), 1);
      t.setDate(Math.min(cellDate.getDate(), new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()));
      moveFocusTo(t);
    } else if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      handleCellSelect(key);
    }
  }, [moveFocusTo, handleCellSelect]);

  // Escape closes the desktop day panel — from the selected cell or from any
  // control inside the panel — and lands focus on that cell as the grid's
  // roving tab stop (Iris D2, 2026-10-01). One handler on the root covers
  // both. It ignores targets outside this DOM (the popovers and the phone
  // sheet portal to body, but React still bubbles their keydown through
  // here) and stands down while any aria-modal dialog is in the document:
  // that dialog owns Escape (the SearchBar.jsx guard).
  const handleRootKeyDown = React.useCallback((e) => {
    if (e.key !== 'Escape' || isMobile || !selectedKey) return;
    if (!e.currentTarget.contains(e.target)) return;
    if (document.querySelector('[aria-modal="true"]')) return;
    const cell = cellRefs.current[selectedKey];
    if (cell && typeof cell.focus === 'function') cell.focus();
    setFocusKey(selectedKey);
    setSelectedKey(null);
  }, [isMobile, selectedKey]);

  // Quick-filter handlers — jump to the right month if needed, then open.
  const jumpToKey = React.useCallback((key) => {
    if (!key) return;
    const parts = key.split('-').map(n => parseInt(n, 10));
    const targetY = parts[0];
    const targetM = parts[1] - 1;
    setCursor({ year: targetY, month: targetM });
    setSelectedKey(key);
    if (window.innerWidth < 768) setSheetOpen(true);
  }, []);

  // Header label.
  const MONTH_NAMES = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE',
                       'JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
  const monthLabel = `${MONTH_NAMES[cursor.month]} ${cursor.year}`;

  // Day-name labels.
  const DAY_NAMES = ['SUN','MON','TUE','WED','THU','FRI','SAT'];

  const showTodayPill = !(cursor.year === today.y && cursor.month === today.m);

  // ─── PALETTE — Category colors driving cell dots + legend ──────────
  // Sourced from the central palette (lib/category-colors.js) so this
  // surface stays in lockstep with EventCard chips, Filter popup, Flows
  // transit map, etc. Inline fallback for SSR / unit-test contexts where
  // window.CATEGORY_COLORS isn't loaded yet.
  const CAT_COLOR = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
    MUSIC:     '#FF4F00',
    CARDS:     '#7A99AC',
    ART:       '#5A7A68',
    FOOD:      '#D57800',
    NIGHTLIFE: '#9F7BE5',
    COMMUNITY: '#395542',
  };

  // Cell sizes — slightly taller than before to fit the headliner line.
  // Mobile compression 2026-06-04 (Marco, third pass): "still too big."
  // Drops min from 72px → 38px so the full 6-row grid clears the fold with
  // breathing room for legend below. 38×~52 (cells are ~52px wide on iPhone SE)
  // meets WCAG 2.5.5 Level-AA 24×24 floor. Count chip moved from bottom-right
  // to top-right so cell padding-bottom can shrink to 4px (no chip clearance
  // needed at bottom).
  const cellMinHeight = isMobile
    ? 'clamp(44px, 11.5vw, 58px)'
    : 'clamp(72px, 15vw, 132px)';

  const amberGold = 'var(--amber-gold, #D57800)';
  const intlOrange = 'var(--international-orange, #FF4F00)';

  // Divider grid background — paints the hairline between cells.
  const dividerBg = darkMode
    ? 'rgba(255,255,255,0.07)'
    : 'rgba(0,0,0,0.07)';

  // Cell base bg.
  const cellBg = darkMode
    ? 'rgba(18,20,20,0.55)'
    : 'rgba(255,255,255,0.65)';

  // Faint weekend wash — Fri/Sat (dow 5,6).
  const weekendWash = darkMode
    ? 'rgba(213, 120, 0, 0.04)'
    : 'rgba(213, 120, 0, 0.05)';

  const inkPrimary = darkMode ? '#fff' : '#1a1a1a';
  const inkQuiet = darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)';
  const inkBleed = darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)';

  // Occasion base rule ink — --on-surface (#E2E2E2 dark / #1A1A1A light): a
  // neutral text ink, never amber (today, selected), orange (NOW, 15+ chip)
  // or a category color. >= 11.2:1 dark / >= 13.9:1 light on every cell state (§7).
  const occasionRuleInk = darkMode ? '#E2E2E2' : '#1A1A1A';

  // ─── DAY PANEL ────────────────────────────────────────────────────
  const panelData = React.useMemo(() => {
    if (!selectedKey) return null;
    const dayEvents = buckets.get(selectedKey) || [];
    const dateParts = selectedKey.split('-').map(n => parseInt(n, 10));
    const dDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
    const FULL_DAYS = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
    const SHORT_MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const headerLabel = `${FULL_DAYS[dDate.getDay()]} ${SHORT_MONTHS[dDate.getMonth()]} ${dDate.getDate()}`;
    const interestCount = (slug) => {
      if (!slug || typeof window.getCachedInterest !== 'function') return 0;
      const cached = window.getCachedInterest(slug);
      return (cached && typeof cached.count === 'number') ? cached.count : 0;
    };
    const byHeat = dayEvents
      .map((ev, i) => ({ ev, i, score: interestCount(ev.slug) }))
      .sort((a, b) => (b.score - a.score) || (a.i - b.i))
      .map(x => x.ev);
    return { dayEvents, byHeat, headerLabel, dDate };
  }, [selectedKey, buckets]);

  const [showAllInPanel, setShowAllInPanel] = React.useState(false);
  React.useEffect(() => { setShowAllInPanel(false); }, [selectedKey]);

  const panelRef = React.useRef(null);
  React.useEffect(() => {
    if (!selectedKey) return;
    if (isMobile) return;
    if (reducedMotion) return;
    const t = setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [selectedKey, isMobile, reducedMotion]);

  React.useEffect(() => {
    if (!panelData || !panelData.dayEvents.length) return;
    const slugs = panelData.dayEvents.map(e => e.slug).filter(Boolean).slice(0, 20);
    if (slugs.length && typeof window.fetchInterestCounts === 'function') {
      window.fetchInterestCounts(slugs);
    }
  }, [panelData]);

  // Headliner per cell — used for the snippet line on busy days.
  const headlinerFor = React.useCallback((dayList) => {
    if (typeof CalendarGrid.headlinerForDay !== 'function') return null;
    const interestFor = (slug) => {
      if (!slug || typeof window.getCachedInterest !== 'function') return 0;
      const cached = window.getCachedInterest(slug);
      return (cached && typeof cached.count === 'number') ? cached.count : 0;
    };
    return CalendarGrid.headlinerForDay(dayList, { interestFor });
  }, [CalendarGrid]);

  // ─── RENDER ──────────────────────────────────────────────────────
  return (
    <div onKeyDown={handleRootKeyDown} style={{
      ...(typeof window.glassStyle === 'function' ? window.glassStyle(darkMode, 'card') : {}),
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
      borderRadius: radius,
      padding: isMobile ? 'clamp(10px, 2vw, 16px)' : 'clamp(14px, 2.5vw, 22px)',
    }}>
      {/* ── Editorial header — month label + monthly stat + quick filters ── */}
      <div style={{
        marginBottom: isMobile ? 6 : 18,
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: isMobile ? 4 : 12, flexWrap: 'wrap',
        }}>
          {/* Left: month nav + label */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4, minWidth: 0,
          }}>
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => stepMonth(-1)}
              className="focus-double-ring"
              style={{
                width: 44, height: 44, padding: 0,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: inkPrimary,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)',
                fontSize: isMobile ? 22 : 26, fontWeight: 800,
                lineHeight: 1,
              }}
            >‹</button>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: isMobile ? 'clamp(16px, 4.2vw, 20px)' : 'clamp(20px, 4.5vw, 30px)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              color: inkPrimary,
              margin: 0,
              lineHeight: 1,
            }}>{monthLabel}</h3>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => stepMonth(1)}
              className="focus-double-ring"
              style={{
                width: 44, height: 44, padding: 0,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: inkPrimary,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)',
                fontSize: isMobile ? 22 : 26, fontWeight: 800,
                lineHeight: 1,
              }}
            >›</button>
          </div>

          {/* Right: month stat */}
          <div
            aria-live="polite"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: isMobile ? 5 : 8,
              fontFamily: 'var(--font-body)',
              fontSize: isMobile ? 9 : 11,
              fontWeight: 700,
              letterSpacing: isMobile ? '0.08em' : '0.12em',
              textTransform: 'uppercase',
              color: inkQuiet,
            }}
          >
            <span aria-hidden="true" style={{
              display: 'inline-block', width: 6, height: 6,
              background: intlOrange,
              borderRadius: 1,
            }} />
            {monthTotal} {monthTotal === 1 ? 'SHOW' : 'SHOWS'} THIS MONTH
          </div>
        </div>

        {/* Quick filters — TODAY · TOMORROW · THIS WEEKEND. Hidden on mobile
            so the grid clears the fold. Power-users on desktop keep them. */}
        {!isMobile && <div style={{
          marginTop: 14,
          display: 'flex', flexWrap: 'wrap', gap: 8,
          alignItems: 'center',
        }}>
          <QuickChip
            label="TODAY"
            count={(buckets.get(todayKey) || []).length}
            onClick={() => jumpToKey(todayKey)}
            isActive={selectedKey === todayKey}
            darkMode={darkMode}
            accent={amberGold}
          />
          <QuickChip
            label="TOMORROW"
            count={(buckets.get(tomorrowKey) || []).length}
            onClick={() => jumpToKey(tomorrowKey)}
            isActive={selectedKey === tomorrowKey}
            darkMode={darkMode}
            accent={amberGold}
          />
          <QuickChip
            label="THIS WEEKEND"
            count={weekendKeys.reduce((s, k) => s + (buckets.get(k) || []).length, 0)}
            onClick={() => jumpToKey(weekendKeys[0])}
            isActive={weekendKeys.includes(selectedKey)}
            darkMode={darkMode}
            accent={intlOrange}
          />
          {showTodayPill && (
            <button
              type="button"
              onClick={() => setCursor({ year: today.y, month: today.m })}
              className="focus-double-ring"
              style={{
                marginLeft: 'auto',
                padding: '8px 12px',
                borderRadius: 100,
                minHeight: 32,
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                border: darkMode ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(0,0,0,0.18)',
                background: 'transparent',
                color: inkPrimary,
              }}
            >← BACK TO {MONTH_NAMES[today.m].slice(0, 3)}</button>
          )}
        </div>}
      </div>

      {/* ── Day-name labels ─────────────────────────────────────── */}
      <div
        role="presentation"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          marginBottom: 1,
        }}
      >
        {DAY_NAMES.map((name, idx) => (
          <div key={name} style={{
            padding: isMobile ? '2px 0' : '8px 0',
            textAlign: 'center',
            fontFamily: 'var(--font-body)',
            fontSize: isMobile ? 9 : 11,
            fontWeight: 700,
            letterSpacing: isMobile ? '0.12em' : '0.16em',
            textTransform: 'uppercase',
            color: (idx === 5 || idx === 6) ? 'var(--amber-gold-ink)' : inkQuiet,
            background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
          }}>{name}</div>
        ))}
      </div>

      {/* ── Grid (7 × 6) ────────────────────────────────────────── */}
      <div
        role="grid"
        aria-label={`Calendar — ${monthLabel}`}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          background: dividerBg,
          border: `1px solid ${dividerBg}`,
        }}
      >
        {(grid.weeks || []).flatMap((row, rowIdx) => row.map((cell, colIdx) => {
          const idxInGrid = rowIdx * 7 + colIdx;
          const isToday = cell.key === todayKey;
          const isSelected = cell.key === selectedKey;
          const isWeekend = cell.dow === 5 || cell.dow === 6;
          const dayList = buckets.get(cell.key) || [];
          const dayCount = dayList.length;
          const mix = mixByKey.get(cell.key) || { dominant: null, mix: [], total: 0 };

          // Bleed cells — quiet, no pointer events.
          if (!cell.inMonth) {
            return (
              <div
                key={cell.key + '-bleed-' + idxInGrid}
                role="gridcell"
                aria-disabled="true"
                style={{
                  minHeight: cellMinHeight,
                  background: cellBg,
                  opacity: 0.32,
                  padding: '8px 10px',
                  pointerEvents: 'none',
                  position: 'relative',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(14px, 2.5vw, 16px)',
                  fontWeight: 600,
                  color: inkBleed,
                  lineHeight: 1,
                }}>{cell.day}</div>
              </div>
            );
          }

          // In-month cell — paint background, weekend wash, today wash.
          let bg = cellBg;
          if (isWeekend) bg = weekendWash + ', ' + cellBg;
          // Stack layers via gradient composition: weekend wash first then cellBg.
          const layeredBg = isWeekend
            ? `linear-gradient(0deg, ${weekendWash}, ${weekendWash}), ${cellBg}`
            : cellBg;

          // Headliner snippet eats vertical room — suppress on mobile where
          // the cell is short (50-72px). Dots + count chip carry the signal.
          const headliner = (!isMobile && dayCount >= 4) ? headlinerFor(dayList) : null;
          const countLabel = dayCount > 99 ? '99+' : String(dayCount);
          // "Wednesday, September 30", not the ISO key — a screen reader read
          // "2026-09-30" as a number.
          // The occasion joins the DAY-NAME clause ("Saturday, October 31,
          // Halloween — 32 events…"): tacked on after "Top: …" it would read
          // as part of the headliner's title (§5).
          const occasion = occasionByKey.get(cell.key) || null;
          const dayName = cell.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
            + (occasion ? `, ${occasion.label}` : '');
          const ariaSummary = dayCount > 0
            ? `${dayName} — ${dayCount} event${dayCount === 1 ? '' : 's'}${headliner ? `. Top: ${headliner.title}` : ''}`
            : `${dayName} — no events`;

          return (
            <button
              key={cell.key}
              ref={el => { cellRefs.current[cell.key] = el; }}
              type="button"
              role="gridcell"
              tabIndex={isSelected ? 0 : (focusKey === cell.key ? 0 : (isToday && !selectedKey ? 0 : -1))}
              aria-label={ariaSummary + (isToday ? ', today' : '')}
              aria-current={isToday ? 'date' : undefined}
              data-occasion-day={occasion ? occasion.slug : undefined}
              aria-selected={isSelected ? 'true' : 'false'}
              aria-pressed={isSelected ? 'true' : 'false'}
              onClick={() => handleCellSelect(cell.key)}
              onKeyDown={(e) => handleCellKeyDown(e, cell.key, cell.date, cell.dow)}
              onMouseEnter={(e) => {
                if (reducedMotion || isMobile) return;
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.background = darkMode
                  ? `linear-gradient(0deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05)), ${cellBg}`
                  : `linear-gradient(0deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04)), ${cellBg}`;
              }}
              onMouseLeave={(e) => {
                if (reducedMotion || isMobile) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = layeredBg;
              }}
              style={{
                position: 'relative',
                minHeight: cellMinHeight,
                background: layeredBg,
                border: 'none',
                // Mobile uses tighter padding so the smaller cells still
                // have room for the count chip without crowding the day digit.
                padding: isMobile ? '3px 4px 3px' : '8px 10px 28px',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: inkPrimary,
                transition: reducedMotion ? 'none' : 'transform 140ms ease-out, background-color 140ms ease-out',
                boxShadow: isSelected
                  ? `inset 0 0 0 2px ${amberGold}`
                  : 'none',
                display: 'block',
              }}
            >
              {/* Selected wash — sits behind content. */}
              {isSelected && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', inset: 0,
                    background: darkMode ? 'rgba(213, 120, 0, 0.12)' : 'rgba(213, 120, 0, 0.10)',
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Top row: date number + today marker */}
              <div style={{
                position: 'relative', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 6,
              }}>
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(15px, 2.8vw, 18px)',
                  fontWeight: isToday ? 900 : 700,
                  color: inkPrimary,
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                  // Today underline.
                  borderBottom: isToday ? `2px solid ${amberGold}` : '2px solid transparent',
                  paddingBottom: 2,
                }}>{cell.day}</span>

                {/* Today badge — orange "NOW" stamp, just for today.
                    Letter-spaced cap so it reads as a flyer mark rather
                    than a dot. Sized to sit on the same baseline as the
                    date number. Hidden on mobile to avoid colliding with
                    the top-right count chip — amber underline + the chip
                    (orange on busy days) carry the today signal alone. */}
                {isToday && !isMobile && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      width: 7, height: 7,
                      background: intlOrange,
                      borderRadius: 1,
                      boxShadow: reducedMotion ? 'none' : `0 0 0 0 ${intlOrange}`,
                      animation: reducedMotion ? 'none' : 'calChipPulse 2s ease-in-out infinite',
                    }} />
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'var(--international-orange-ink)',
                      lineHeight: 1,
                    }}>NOW</span>
                  </span>
                )}

                {/* Category dots — up to 3, color-coded by dominant categories.
                    Hidden on mobile (count chip already carries the dominant
                    category color, so it doubles as the category signal). */}
                {!isToday && !isMobile && mix.mix.length > 0 && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex', gap: 3, flexShrink: 0,
                    }}
                  >
                    {mix.mix.map(cat => (
                      <span
                        key={cat}
                        style={{
                          width: 6, height: 6,
                          background: CAT_COLOR[cat] || CAT_COLOR.COMMUNITY,
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </span>
                )}
              </div>

              {/* Today gets the dots ON A SECOND ROW so the orange marker stays
                  alone — desktop only; mobile drops dots to keep cells tiny. */}
              {isToday && !isMobile && mix.mix.length > 0 && (
                <div style={{
                  marginTop: 6,
                  display: 'inline-flex', gap: 3,
                }} aria-hidden="true">
                  {mix.mix.map(cat => (
                    <span
                      key={cat}
                      style={{
                        width: 6, height: 6,
                        background: CAT_COLOR[cat] || CAT_COLOR.COMMUNITY,
                        borderRadius: 1,
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Headliner snippet — only on days with 4+ events. Truncated. */}
              {headliner && headliner.title && (
                <div
                  style={{
                    marginTop: 8,
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(10px, 1.6vw, 11px)',
                    fontWeight: 600,
                    color: inkPrimary,
                    opacity: 0.78,
                    lineHeight: 1.25,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    letterSpacing: '-0.005em',
                    position: 'relative',
                  }}
                >{headliner.title}</div>
              )}

              {/* Count chip — bottom-right on desktop (room to breathe), top-right
                  on mobile (cells too short to anchor at bottom). Only when count > 0.
                  Orange highlight reserved for the day's "big nights" (15+).
                  Quieter days carry the dominant-category color so the eye
                  can still scan the calendar by category at a glance — on mobile
                  this color also replaces the hidden dot row, doubling as the
                  category signal. */}
              {dayCount > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    ...(isMobile
                      ? { top: 3, right: 3, minWidth: 18, height: 14, padding: '0 4px', fontSize: 9 }
                      : { bottom: 8, right: 8, minWidth: 22, height: 18, padding: '0 6px', fontSize: 10 }),
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: dayCount >= 15
                      ? intlOrange
                      : (mix.dominant ? CAT_COLOR[mix.dominant] : amberGold),
                    color: (dayCount < 15 && mix.dominant && window.categoryTextColor)
                      ? window.categoryTextColor(CAT_COLOR[mix.dominant])
                      : 'var(--on-international-orange)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    lineHeight: 1,
                    borderRadius: 2,
                    // Slight pulse on the biggest days only — under-the-fold
                    // signal that something is happening. Reduced-motion safe
                    // via the @media query injected below.
                    animation: dayCount >= 30 && !reducedMotion ? 'calChipPulse 2.6s ease-in-out infinite' : 'none',
                  }}
                >{countLabel}</span>
              )}

              {/* Occasion base rule — 3px on the bottom edge, the one place
                  left empty at both sizes (phones hide dots, headliner and
                  NOW). Empty + aria-hidden: the cell's text is unchanged and
                  the occasion is spoken by the aria-label above. Steps inside
                  the 2px amber ring when selected. Rides the cell's hover lift;
                  no motion of its own. Forced colors: the injected <style>. */}
              {occasion && (
                <span
                  data-occasion-rule=""
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: isSelected ? 2 : 0,
                    right: isSelected ? 2 : 0,
                    bottom: isSelected ? 2 : 0,
                    height: 3,
                    background: occasionRuleInk,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </button>
          );
        }))}
      </div>

      {/* ── Legend — teaches the dot system once. Desktop only.
          Mobile dropped the dot row from cells, so the legend below has
          nothing to teach — and the reclaimed ~30px of vertical room
          buys the full 6-row grid clearance of one iPhone viewport. ─── */}
      {!isMobile && <div style={{
        marginTop: isMobile ? 4 : 14,
        display: 'flex', flexWrap: 'wrap',
        gap: isMobile ? '3px 8px' : '8px 16px',
        alignItems: 'center',
        fontFamily: 'var(--font-body)',
        fontSize: isMobile ? 8 : 10,
        fontWeight: 700,
        letterSpacing: isMobile ? '0.06em' : '0.12em',
        textTransform: 'uppercase',
        color: inkQuiet,
      }}>
        <span style={{ marginRight: isMobile ? 2 : 4 }}>KEY ·</span>
        {Object.entries(CAT_COLOR).map(([cat, color]) => (
          <span key={cat} style={{
            display: 'inline-flex', alignItems: 'center', gap: isMobile ? 4 : 6,
          }}>
            <span aria-hidden="true" style={{
              width: isMobile ? 6 : 8, height: isMobile ? 6 : 8,
              background: color, borderRadius: 1, flexShrink: 0,
            }} />
            <span>{cat}</span>
          </span>
        ))}
      </div>}

      {/* ── Inline panel (desktop) ──────────────────────────────── */}
      {!isMobile && selectedKey && panelData && (
        <div
          ref={panelRef}
          role="region"
          aria-label={`Events on ${panelData.headerLabel}`}
          style={{
            marginTop: 18,
            padding: 'clamp(16px, 3vw, 24px)',
            background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: Math.max(radius - 4, 8),
            animation: reducedMotion ? 'none' : 'calPanelOpen 180ms ease-out',
          }}
        >
          <DayPanelBody
            data={panelData}
            darkMode={darkMode}
            radius={radius}
            showAll={showAllInPanel}
            onShowAll={() => setShowAllInPanel(true)}
            onClose={() => setSelectedKey(null)}
            inkPrimary={inkPrimary}
            inkQuiet={inkQuiet}
            amberGold={amberGold}
            intlOrange={intlOrange}
            EventCard={EventCard}
            isSheet={false}
            CAT_COLOR={CAT_COLOR}
          />
        </div>
      )}

      {/* ── Mobile bottom sheet ─────────────────────────────────── */}
      {isMobile && selectedKey && sheetOpen && panelData && (
        typeof window !== 'undefined' && window.ReactDOM && window.ReactDOM.createPortal
          ? window.ReactDOM.createPortal(
            <MobileSheet
              data={panelData}
              darkMode={darkMode}
              radius={radius}
              reducedMotion={reducedMotion}
              showAll={showAllInPanel}
              onShowAll={() => setShowAllInPanel(true)}
              onClose={() => { setSheetOpen(false); setTimeout(() => setSelectedKey(null), 220); }}
              inkPrimary={inkPrimary}
              inkQuiet={inkQuiet}
              amberGold={amberGold}
              intlOrange={intlOrange}
              EventCard={EventCard}
              CAT_COLOR={CAT_COLOR}
            />,
            document.body
          )
          : null
      )}

      <style>{`
        @keyframes calPanelOpen {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes calSheetIn {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes calBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes calChipPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @media (forced-colors: active) {
          [role="gridcell"]:focus-visible {
            outline: 2px solid Highlight !important;
            outline-offset: -2px !important;
          }
          /* High Contrast strips backgrounds — without this the occasion
             rule vanishes. !important beats the inline ink. */
          [data-occasion-rule] {
            forced-color-adjust: none;
            background: CanvasText !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ── QuickChip — TODAY / TOMORROW / THIS WEEKEND filter chip ────── */
function QuickChip({ label, count, onClick, isActive, darkMode, accent }) {
  const React = window.React;
  const [hov, setHov] = React.useState(false);
  const fg = darkMode ? '#fff' : '#1a1a1a';
  const dimFg = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const has = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="focus-double-ring"
      aria-pressed={isActive ? 'true' : 'false'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 14px',
        minHeight: 36,
        background: isActive
          ? accent
          : (hov
              ? (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')
              : 'transparent'),
        color: isActive ? 'var(--on-international-orange)' : (has ? fg : dimFg),
        border: isActive
          ? `2px solid ${accent}`
          : (darkMode ? '2px solid rgba(255,255,255,0.16)' : '2px solid rgba(0,0,0,0.12)'),
        borderRadius: 100,
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        transition: 'all 140ms ease-out',
      }}
    >
      <span>{label}</span>
      <span aria-label={`${count} events`} style={{
        minWidth: 18, height: 16,
        padding: '0 5px',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: isActive
          ? 'rgba(255,255,255,0.25)'
          : (darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
        color: isActive ? 'var(--on-international-orange)' : (has ? fg : dimFg),
        fontFamily: 'var(--font-body)',
        fontSize: 10, fontWeight: 800,
        borderRadius: 2,
        lineHeight: 1,
      }}>{count}</span>
    </button>
  );
}

/* ── DayPanelBody ─── shared by inline panel and bottom sheet ─────── */
function DayPanelBody({
  data, darkMode, radius, showAll, onShowAll, onClose,
  inkPrimary, inkQuiet, amberGold, intlOrange, EventCard, isSheet, CAT_COLOR, titleRef,
}) {
  const React = window.React;
  const { dayEvents, byHeat, headerLabel } = data;
  const list = showAll ? byHeat : byHeat.slice(0, 6);
  const hasMore = byHeat.length > 6 && !showAll;
  const isEmpty = dayEvents.length === 0;

  // Category breakdown for the header chip-strip.
  const breakdown = React.useMemo(() => {
    const out = new Map();
    for (const ev of dayEvents) {
      const cat = (ev.category || 'COMMUNITY').toUpperCase();
      out.set(cat, (out.get(cat) || 0) + 1);
    }
    return [...out.entries()].sort((a, b) => b[1] - a[1]);
  }, [dayEvents]);

  return (
    <React.Fragment>
      {/* Header — editorial date + count + category breakdown */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, marginBottom: 18, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          {/* The sheet lands focus here on open (tabIndex -1: reachable by
              script, skipped by Tab), so the first thing announced is the day. */}
          <div ref={titleRef} tabIndex={isSheet ? -1 : undefined} style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(20px, 3.5vw, 26px)',
            fontWeight: 900,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: inkPrimary,
            lineHeight: 1,
            outline: 'none',
          }}>{headerLabel}</div>
          {!isEmpty && (
            <div style={{
              marginTop: 8,
              display: 'flex', flexWrap: 'wrap', gap: '6px 12px',
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: inkQuiet,
              alignItems: 'center',
            }}>
              <span style={{ color: inkPrimary, fontWeight: 800 }}>
                {dayEvents.length} {dayEvents.length === 1 ? 'SHOW' : 'SHOWS'}
              </span>
              {breakdown.map(([cat, n]) => (
                <span key={cat} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                  <span aria-hidden="true" style={{
                    width: 6, height: 6,
                    background: (CAT_COLOR && CAT_COLOR[cat]) || amberGold,
                    borderRadius: 1,
                  }} />
                  <span>{n} {cat}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        {isSheet && (
          <button
            type="button"
            aria-label="Close day panel"
            onClick={onClose}
            className="focus-double-ring"
            style={{
              width: 44, height: 44, padding: 0,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: inkPrimary,
              fontSize: 22, lineHeight: 1,
              flexShrink: 0,
            }}
          >✕</button>
        )}
      </div>

      {/* Empty state */}
      {isEmpty ? (
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          color: inkQuiet,
          lineHeight: 1.5,
        }}>
          Nothing on the books for {headerLabel}. Yet.{' '}
          <a
            href="/#submit"
            style={{
              color: 'var(--amber-gold-ink)',
              textDecoration: 'underline',
              textUnderlineOffset: 4,
              fontWeight: 700,
            }}
          >or submit ↗</a>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
          gap: 12,
        }}>
          {list.map((ev, i) => (
            EventCard ? (
              <EventCard
                key={ev.slug || `${ev.title}-${i}`}
                event={ev}
                darkMode={darkMode}
                index={i}
                radius={Math.max(radius - 4, 8)}
              />
            ) : (
              <div key={ev.slug || i} style={{
                padding: 12,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: 8,
                color: inkPrimary,
                fontFamily: 'var(--font-body)', fontSize: 13,
              }}>{ev.title}</div>
            )
          ))}
        </div>
      )}

      {/* SHOW ALL link */}
      {hasMore && (
        <button
          type="button"
          onClick={onShowAll}
          className="focus-double-ring"
          style={{
            marginTop: 16,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 0',
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--amber-gold-ink)',
            textDecoration: 'underline',
            textUnderlineOffset: 4,
          }}
        >SHOW ALL ({byHeat.length}) →</button>
      )}
    </React.Fragment>
  );
}

/* ── MobileSheet ──────────────────────────────────────────────────── */
function MobileSheet({
  data, darkMode, radius, reducedMotion, showAll, onShowAll, onClose,
  inkPrimary, inkQuiet, amberGold, intlOrange, EventCard, CAT_COLOR,
}) {
  const React = window.React;
  const panelRef = React.useRef(null);
  const titleRef = React.useRef(null);

  // aria-modal hides the rest of the page from assistive tech, so focus has
  // to come along: land on the day title, and hand focus back to whatever
  // opened the sheet (the day cell) when it closes. Captured once on mount —
  // onClose is a fresh arrow every render, so it cannot own this effect.
  React.useEffect(() => {
    const opener = document.activeElement;
    if (titleRef.current) titleRef.current.focus({ preventScroll: true });
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  // onClose is a fresh arrow every render; read it through a ref so the key
  // listener registers ONCE, at mount — before any popover opened from
  // inside the sheet adds its own window listener. Listeners fire in
  // registration order and React flushes that popover's close between
  // them in a browser, so a later-registered sheet listener could miss the
  // dialog the stand-down below looks for.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    const onKey = (e) => {
      // A RulesPopover opened from an ENTRY chip in the sheet is its own
      // aria-modal dialog (portaled to body, outside panelRef) and owns
      // Escape and Tab until it closes: the WHOLE handler stands down while
      // a foreign one is in the document (Iris D3, 2026-10-01). The sheet
      // is aria-modal too — its own panel is not foreign.
      const foreignModal = [...document.querySelectorAll('[aria-modal="true"]')]
        .some((el) => el !== panelRef.current);
      if (foreignModal) return;
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      // Keep Tab inside the sheet while it is open.
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === titleRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Events on ${data.headerLabel}`}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 1800,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'stretch',
      }}
    >
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          animation: reducedMotion ? 'none' : 'calBackdropIn 180ms ease-out',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: '20px 16px 32px',
          background: darkMode ? 'rgba(18,20,20,0.96)' : 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          animation: reducedMotion ? 'none' : 'calSheetIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div aria-hidden="true" style={{
          width: 36, height: 4, borderRadius: 2,
          background: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)',
          margin: '0 auto 16px',
        }} />
        <DayPanelBody
          data={data}
          darkMode={darkMode}
          radius={radius}
          showAll={showAll}
          onShowAll={onShowAll}
          onClose={onClose}
          inkPrimary={inkPrimary}
          inkQuiet={inkQuiet}
          amberGold={amberGold}
          intlOrange={intlOrange}
          EventCard={EventCard}
          isSheet={true}
          CAT_COLOR={CAT_COLOR}
          titleRef={titleRef}
        />
      </div>
    </div>
  );
}

window.CalendarView = CalendarView;
